<script lang="ts" setup>
import { Delta } from "@vueup/vue-quill";
import {
  ChecklistCriteria,
  CriteriaInstance,
  ParagraphPositionData,
} from "types/Checklist";
import { EvaluationRelevantInstance } from "types/EvaluationResults";

const props = defineProps({
  paragraphPositionData: {
    type: Object as PropType<ParagraphPositionData>,
    required: true,
  },
  criteriaInstance: {
    type: Object as PropType<CriteriaInstance>,
    required: true,
  },
  checklistCriteria: {
    type: Object as PropType<ChecklistCriteria>,
    required: true,
  },
});

const emits = defineEmits(["reportProblem"]);

const documentId = inject("documentId") as any;
const evaluationResultsStore = useStepResultsStore(documentId);
const { relevantInstancesForParagraph, criteriaConfig, showAllInstances } =
  storeToRefs(evaluationResultsStore);

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isTeacherViewMode } = storeToRefs(documentViewModeStore);

const onUpdateParagraph = (value: number, index: number) => {
  try {
    evaluationResultsStore.updateManualParagraphPoints(index, value);
  } catch (error) {}
};

const onReportProblem = (ev: any, isControl: boolean) => {
  emits("reportProblem", ev, isControl);
};

const allRelevantInstances = computed(() => {
  return relevantInstancesForParagraph.value(
    props.paragraphPositionData.position
  );
});

// If we aren't showing all instances then we want to only show the first one.
const relevantInstances = computed(() => {
  let instances = sortedRelevantInstances.value(allRelevantInstances.value);

  if (isTeacherViewMode.value != true || true) {
    instances = instances.filter((instance) => {
      return (
        useDoesRelevantInstanceHaveActiveSubtype({
          relevantInstance: instance,
          documentId,
          activeTypesOverride: props.checklistCriteria.activeTypes,
          activeSubTypesOverride: props.checklistCriteria.activeSubTypes,
          debug: false,
        }).value >= 0
      );
    });
  }

  if (
    isTeacherViewMode.value == true &&
    criteriaConfig.value?.shouldHideExtraInstances == true &&
    showAllInstances.value(props.paragraphPositionData.position) == false
  ) {
    return instances.slice(0, 1);
  }

  return instances;
});

const documentActiveTypeStore = useDocumentActiveTypeStore(documentId);
const { hasActiveTypes, activeSubTypes, activeTypes, activeCriteria } =
  storeToRefs(documentActiveTypeStore);

const sortedRelevantInstances = computed(() => {
  return (instances: EvaluationRelevantInstance[]) => {
    return instances.sort((a, b) => {
      const aHasActiveSubType = useDoesRelevantInstanceHaveActiveSubtype({
        relevantInstance: a,
        documentId,
        activeTypesOverride: props.checklistCriteria.activeTypes,
        activeSubTypesOverride: props.checklistCriteria.activeSubTypes,
        debug: false,
      });
      const bHasActiveSubType = useDoesRelevantInstanceHaveActiveSubtype({
        relevantInstance: b,
        documentId,
        activeTypesOverride: props.checklistCriteria.activeTypes,
        activeSubTypesOverride: props.checklistCriteria.activeSubTypes,
        debug: false,
      });

      return bHasActiveSubType.value - aHasActiveSubType.value;
    });
  };
});

watch(relevantInstances, () => {
  highlightRelevantInstances();
});

onMounted(() => {
  highlightRelevantInstances();
});

const { addPositions, removePositions } = useDocumentHighlightStore(documentId);
const documentTokenIdPositionMap =
  useDocumentTokenIdPositionMapStore(documentId);
const { tokenGlobalPositionFromId } = storeToRefs(documentTokenIdPositionMap);

const highlightRelevantInstances = () => {
  // loop through all relevant instances recursive and remove the positions
  // Loop through relevantInstance that are being shown recursive and add the positions
  for (var relevantInstance of allRelevantInstances.value) {
    changePositionHighlightRecursive(
      relevantInstance,
      criteriaConfig.value?.highlightInstances == true,
      false
    );
  }

  for (var relevantInstance of relevantInstances.value) {
    changePositionHighlightRecursive(
      relevantInstance,
      criteriaConfig.value?.highlightInstances == true,
      true
    );
  }
};

const changePositionHighlightRecursive = (
  relevantInstance: EvaluationRelevantInstance,
  highlightInstancesConfig: boolean,
  isHighlighting: boolean
) => {
  if (relevantInstance.type == "paragraph") {
    if (relevantInstance.criteria && relevantInstance.criteria.length > 0) {
      for (var criteria of relevantInstance.criteria) {
        for (var relevantInstance of criteria.relevantInstances) {
          changePositionHighlightRecursive(
            relevantInstance,
            criteria.config.highlightInstances,
            isHighlighting
          );
        }
      }
    }
  }

  if (highlightInstancesConfig == true) {
    var positions = relevantInstance.tokens.map((token) =>
      tokenGlobalPositionFromId.value(token.id)
    );

    var filteredPositions = positions.filter(
      (position) => position != null
    ) as number[];

    if (isHighlighting == true) {
      addPositions(filteredPositions);
    } else {
      removePositions(filteredPositions);
    }
  }
};

const missingComment = computed(() => {
  return `missing ${props.checklistCriteria.label}`;
});

const { setAnnotationBeingEdited, createDefaultAnnotation } =
  useAnnotationFormState();

const { setSelection } = useDocumentSelectionStore(documentId);

const documentParagraphsStore = useDocumentParagraphsStore(documentId);
const { paragraphIdFromPosition } = storeToRefs(documentParagraphsStore);

const documentEvaluativeParagraphStore =
  useDocumentEvaluativeParagraphsStore(documentId);
const { getParagraphIdFromParagraphPosition } = storeToRefs(
  documentEvaluativeParagraphStore
);

const paragraph = computed(() => {
  const paragraphId = getParagraphIdFromParagraphPosition.value(
    props.paragraphPositionData.position
  );

  if (paragraphId == undefined) return;

  const documentParagraphStore = useDocumentParagraphStore(
    documentId,
    paragraphId
  );
  const { paragraph: matchingParagraph } = storeToRefs(documentParagraphStore);

  return matchingParagraph.value;
});

const createAnnotation = () => {
  // OK where do I, if at all, map a paragraph position to an id?

  if (paragraph.value == undefined) return;

  const tokens = paragraph.value.tokens;

  const annotation = createDefaultAnnotation();

  annotation.forceInline = true;
  annotation.forceAfterToken = true;
  annotation.isEndLine = true;
  annotation.lastTokenGlobalPosition = tokens[tokens.length - 1].globalPosition;
  annotation.paragraphId = paragraph.value.id;

  return annotation;
};

const annotateInstances = () => {
  const commentOnlyAnnotation = createAnnotation();

  setAnnotationBeingEdited(documentId, commentOnlyAnnotation);
};

const { saveAnnotation } = useSaveAnnotation(documentId);

const addAnnotation = (comment: string) => {
  const commentOnlyAnnotation = createAnnotation();

  if (commentOnlyAnnotation == undefined) return;

  commentOnlyAnnotation.commentObject = new Delta().insert(comment).ops;
  commentOnlyAnnotation.quickComment = comment;

  if (props.checklistCriteria.useSmartAnnotations) {
    commentOnlyAnnotation.isSmartAnnotation = true;
  }

  saveAnnotation(
    commentOnlyAnnotation,
    useIsAnnotationCommentLong(commentOnlyAnnotation)
  ).then();

  setSelection(undefined, undefined);
};

const showNoneFoundMessage = computed(() => {
  if (props.checklistCriteria.evaluationMetricId == undefined) return false;

  if (props.checklistCriteria.showCommentWhenMissing == false) return false;

  return relevantInstances.value.length == 0;
});
</script>

<template>
  <div
    class="flex flex-col border-b-2 py-4 first:pt-0 last:pb-0 last:border-b-0"
    :key="paragraphPositionData.position"
  >
    <div
      class="flex flex-row"
      :data-step-id="`paragraph-score-container-${paragraphPositionData.position}`"
    >
      <div
        class="grow mr-2 font-bold select-none flex flex-row items-center -mt-[1px] leading-[30px]"
      >
        {{ paragraphPositionData.longLabel }}
      </div>
      <BaseScoreInput
        v-if="isTeacherViewMode"
        @click.stop.prevent
        @input="onUpdateParagraph($event, paragraphPositionData.position)"
        :key="paragraphPositionData.position"
        :value="
          paragraphPositionData.manualScore ?? paragraphPositionData.score ?? 0
        "
        :tabIndex="(parseInt(paragraphPositionData.position as any) + 1) * 10"
        :width="40"
        :height="30"
        :max-points="criteriaInstance.pointsPerInstance"
        :show-points-label="true"
      />
    </div>
    <div v-if="showNoneFoundMessage" class="px-4">
      <DocumentPanelChecklistParagraphNoneFound
        :position="paragraphPositionData.position"
        :checklist-criteria="checklistCriteria"
      />
    </div>
    <EvaluationResultsInstancesList
      v-if="checklistCriteria.evaluationMetricId && criteriaConfig"
      :key="paragraphPositionData.position"
      :criteria-instance="criteriaInstance"
      :relevant-instances="relevantInstances"
      :criteria-config="(criteriaConfig as any)"
      :paragraph-position="paragraphPositionData.position"
      :show-all="
        showAllInstances(paragraphPositionData.position) ||
        isTeacherViewMode != true
      "
      @report-problem="onReportProblem"
    />
    <p
      v-if="isTeacherViewMode != true && allRelevantInstances.length > 1"
      class="text-danger-default text-center my-2"
    >
      Choose one to be evaluated.
    </p>
    <BaseTextButton
      color="primary"
      @click="
        evaluationResultsStore.toggleShowAllInstances(
          paragraphPositionData.position
        )
      "
      v-if="
        isTeacherViewMode &&
        criteriaConfig?.shouldHideExtraInstances &&
        allRelevantInstances.length > 1
      "
    >
      {{
        showAllInstances(paragraphPositionData.position)
          ? "Show less"
          : "Show more"
      }}
    </BaseTextButton>
  </div>
</template>

<style scoped></style>
